import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { PageHeader } from "../../components/StaffLayout";
import { CaretLeft, CaretRight, Calendar as CalIcon } from "@phosphor-icons/react";

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Mon=0
  x.setDate(x.getDate() - day);
  x.setHours(0,0,0,0);
  return x;
}

export default function CalendarView() {
  const [doctors, setDoctors] = useState([]);
  const [docFilter, setDocFilter] = useState("");
  const [mode, setMode] = useState("week");
  const [anchor, setAnchor] = useState(new Date());
  const [appts, setAppts] = useState([]);

  useEffect(() => { api.get("/doctors").then(r=>setDoctors(r.data)); }, []);

  const range = useMemo(() => {
    if (mode === "day") {
      const d = new Date(anchor); d.setHours(0,0,0,0);
      const next = new Date(d); next.setDate(next.getDate()+1);
      return [d, next];
    }
    const wstart = startOfWeek(anchor);
    const wend = new Date(wstart); wend.setDate(wend.getDate()+7);
    return [wstart, wend];
  }, [mode, anchor]);

  useEffect(() => {
    const [from, to] = range;
    const params = new URLSearchParams({ from_date: from.toISOString(), to_date: to.toISOString() });
    if (docFilter) params.append("doctor_id", docFilter);
    api.get(`/appointments?${params.toString()}`).then(r=>setAppts(r.data));
  }, [range, docFilter]);

  const days = useMemo(() => {
    const [from, to] = range;
    const out = [];
    const cur = new Date(from);
    while (cur < to) { out.push(new Date(cur)); cur.setDate(cur.getDate()+1); }
    return out;
  }, [range]);

  const apptsByDay = useMemo(() => {
    const m = {};
    appts.forEach(a => {
      const k = a.starts_at.slice(0,10);
      m[k] = m[k] || [];
      m[k].push(a);
    });
    return m;
  }, [appts]);

  const move = (dir) => {
    const d = new Date(anchor);
    if (mode === "day") d.setDate(d.getDate() + dir);
    else d.setDate(d.getDate() + 7*dir);
    setAnchor(d);
  };

  return (
    <div data-testid="calendar-page">
      <PageHeader title="Calendar" subtitle="Day & week view with multi-doctor filter"
        actions={
          <div className="flex items-center gap-2">
            <select value={docFilter} onChange={(e)=>setDocFilter(e.target.value)}
              className="border border-zinc-300 rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:border-zinc-900" data-testid="cal-doctor-filter">
              <option value="">All doctors</option>
              {doctors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <div className="inline-flex border border-zinc-300 rounded-sm">
              <button onClick={()=>setMode("day")} className={`px-3 py-2 text-xs uppercase tracking-wider font-bold ${mode==="day"?"bg-zinc-900 text-white":"text-zinc-700"}`} data-testid="cal-mode-day">Day</button>
              <button onClick={()=>setMode("week")} className={`px-3 py-2 text-xs uppercase tracking-wider font-bold ${mode==="week"?"bg-zinc-900 text-white":"text-zinc-700"}`} data-testid="cal-mode-week">Week</button>
            </div>
          </div>
        }/>
      <div className="px-6 sm:px-10 py-6">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={()=>move(-1)} className="border border-zinc-300 rounded-sm w-9 h-9 inline-flex items-center justify-center hover:border-zinc-900" data-testid="cal-prev"><CaretLeft size={16} weight="bold"/></button>
          <button onClick={()=>setAnchor(new Date())} className="text-xs uppercase tracking-[0.25em] font-bold text-zinc-700 px-3 py-2 border border-zinc-300 rounded-sm hover:border-zinc-900" data-testid="cal-today">Today</button>
          <button onClick={()=>move(1)} className="border border-zinc-300 rounded-sm w-9 h-9 inline-flex items-center justify-center hover:border-zinc-900" data-testid="cal-next"><CaretRight size={16} weight="bold"/></button>
          <div className="ml-2 font-bold tracking-tight" style={{fontFamily:"Manrope, sans-serif"}}>
            {mode==="day" ? anchor.toLocaleDateString(undefined, {weekday:"long", month:"long", day:"numeric", year:"numeric"}) :
              `${days[0].toLocaleDateString(undefined,{month:"short",day:"numeric"})} - ${days[days.length-1].toLocaleDateString(undefined,{month:"short",day:"numeric",year:"numeric"})}`}
          </div>
        </div>

        <div className={`grid gap-3 ${mode==="day"?"grid-cols-1":"grid-cols-1 sm:grid-cols-2 lg:grid-cols-7"}`}>
          {days.map((d) => {
            const k = d.toISOString().slice(0,10);
            const list = (apptsByDay[k] || []).sort((a,b)=>a.starts_at.localeCompare(b.starts_at));
            const isToday = k === new Date().toISOString().slice(0,10);
            return (
              <div key={k} className={`bg-white border ${isToday?"border-[#E60000]":"border-zinc-200"} rounded-sm min-h-[120px]`}
                data-testid={`cal-day-${k}`}>
                <div className={`px-3 py-2 border-b border-zinc-100 flex items-center justify-between ${isToday?"bg-red-50":""}`}>
                  <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">{d.toLocaleDateString(undefined,{weekday:"short"})}</div>
                  <div className={`text-lg font-black tracking-tighter ${isToday?"text-[#E60000]":"text-zinc-900"}`} style={{fontFamily:"Manrope, sans-serif"}}>
                    {d.getDate()}
                  </div>
                </div>
                <div className="p-2 space-y-1">
                  {list.length === 0 && <div className="text-xs text-zinc-400 text-center py-2"><CalIcon size={14} weight="bold" className="inline mb-0.5"/></div>}
                  {list.map(a => (
                    <div key={a.id} className="text-[11px] border-l-2 pl-2 py-1 hover:bg-zinc-50"
                      style={{borderLeftColor: a.doctor_obj?.color || "#E60000"}}>
                      <div className="font-bold">{a.starts_at.slice(11,16)} Â· {a.patient?.name || "â€”"}</div>
                      <div className="text-zinc-500 truncate">{a.treatment || "Consultation"}{a.doctor_obj ? ` Â· ${a.doctor_obj.name}` : ""}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
